export default function productForm(initialData) {
  return {
    // Gift card state
    errorAlert: false,
    errorMessage: '',
    giftCardRecipientEmail: '',
    giftCardRecipientName: '',
    giftCardMessage: '',
    giftCardSendOn: '',
    giftCardDrawerLoading: false,

    // Initial state from Liquid (overrides defaults above)
    ...initialData,

    initProductForm(presetVariantId) {
      if (presetVariantId) {
        this.handleProductFormChange(presetVariantId);
        return;
      }

      this.handleProductFormChange();
    },

    // Update page when variant selection changes
    handleProductFormChange(preselectedVariantId) {
      this.setOptionsFromPreselectedVariantId(preselectedVariantId);

      const selectedVariant = this.setSelectedVariant();

      this.setUnavailableOptions();

      this.setDefaultsFromSelectedVariant(selectedVariant);

      this.setAllOptionsSelected();

      this.updateUrlParameters();

      this.calculatedPrice = this.quantity * this.currentVariantPrice;
    },

    // Check if preselectedVariantId exists and set options
    setOptionsFromPreselectedVariantId(preselectedVariantId) {
      if (!preselectedVariantId) return;

      const selectedVariant = this.product.variants.find((variant) =>
        variant.id === preselectedVariantId
      );

      if (!selectedVariant) return;

      this.currentVariantId = preselectedVariantId;

      const optionsSize = this.product.options.length;
      for (let i = 1; i <= optionsSize; i++) {
        this[`option${i}`] = selectedVariant[`option${i}`];
      }
    },

    // Find options that are not available based on selected options
    setUnavailableOptions() {
      const generateOptionCombinations = () => {
        const options = this.product.options.map((option, index) => {
          return [...new Set(this.product.variants.map(variant => variant[`option${index + 1}`]))];
        });

        return options.reduce((acc, option, index) => {
          return acc.flatMap(combination => {
            return option.map(optionValue => ({
              ...combination, [`option${index + 1}`]: optionValue
            }));
          });
        }, [{}]);
      };

      const findMatchingVariants = (excludeOptionIndex) => {
        return this.product.variants.filter(variant => {
          if (variant.available) return false;

          if (this.product.options.length === 1) return true;

          for (let i = 1; i <= this.product.options.length; i++) {
            if (i === excludeOptionIndex) continue;
            if (variant[`option${i}`] !== this[`option${i}`]) return false;
          }

          return true;
        });
      };

      const allCombinations = generateOptionCombinations();

      const nonExistentVariants = allCombinations.filter(combination => {
        return !this.product.variants.some(variant => {
          return Object.keys(combination).every(optionKey => {
            return variant[optionKey] === combination[optionKey];
          });
        });
      });

      for (let i = 1; i <= this.product.options.length; i++) {
        const unavailableOptionsSet = new Set();

        findMatchingVariants(i).forEach(variant => {
          unavailableOptionsSet.add(variant[`option${i}`]);
        });

        nonExistentVariants.forEach(variant => {
          const isMatch = Object.keys(variant).every(optionKey => {
            const optionIndex = parseInt(optionKey.replace('option', ''));
            return optionIndex === i || variant[optionKey] === this[`option${optionIndex}`];
          });

          if (isMatch) unavailableOptionsSet.add(variant[`option${i}`]);
        });

        this[`unavailableOptions${i}`] = Array.from(unavailableOptionsSet);
      }
    },

    // Update values based on selected variant
    setDefaultsFromSelectedVariant(selectedVariant) {
      if (!selectedVariant) {
        this.currentVariantExists = false;
        return;
      }

      this.currentVariantAvailable = selectedVariant.available;
      this.currentVariantExists = true;
      this.currentVariantId = selectedVariant.id;
      this.currentVariantPrice = selectedVariant.price;
      this.currentVariantComparePrice = selectedVariant.compare_at_price;
      this.currentVariantSku = selectedVariant.sku;
      this.currentVariantTitle = selectedVariant.title;

      const variantData = this.variants[this.currentVariantId];
      if (variantData) {
        this.currentVariantInventoryQuantity = variantData.inventoryQuantity;
      }

      this.currentVariantFeaturedImageId = selectedVariant.featured_image?.id ?? null;
      this.currentVariantFeaturedMediaId = selectedVariant.featured_media?.id ?? null;
    },

    // Update allOptionsSelected if all options are selected
    setAllOptionsSelected() {
      const optionsSize = this.product.options.length;
      const selectedOptions = [];

      for (let i = 1; i <= optionsSize; i++) {
        selectedOptions.push(this[`option${i}`]);
      }

      this.allOptionsSelected = selectedOptions.every(option => Boolean(option));
    },

    // Set selectedVariant based on selected options
    setSelectedVariant() {
      const optionsSize = this.product.options.length;

      const selectedVariant = this.product.variants.find(variant => {
        for (let i = 1; i <= optionsSize; i++) {
          if (variant[`option${i}`] !== this[`option${i}`]) return false;
        }
        return true;
      });

      return selectedVariant;
    },

    // Add variant id to URL parameters
    updateUrlParameters() {
      if (!window.location.pathname.includes('/products/')) return;
      if (!this.allOptionsSelected) return;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('variant', this.currentVariantId);

      history.replaceState(null, '', window.location.pathname + '?' + searchParams.toString());
    },

    scrollToVariantMedia() {
      const id = this.currentVariantFeaturedImageId?.toString();
      if (!id) return;

      // Desktop: scroll image into view
      const desktopImage = document.querySelector(`[data-gallery="desktop"] [data-media-id="${id}"]`);
      if (desktopImage) {
        desktopImage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      // Mobile: slide swiper to matching index
      const mobileGallery = document.querySelector('[data-gallery="mobile"]');
      if (!mobileGallery?.dataset.swiperId) return;

      const swiper = Alpine.store('swipers').getSwiperState(mobileGallery.dataset.swiperId).instance;
      if (!swiper) return;

      const index = Array.from(mobileGallery.querySelectorAll('.swiper-slide'))
        .findIndex(slide => slide.dataset.mediaId === id);

      if (index !== -1) swiper.slideTo(index);
    },

    async addGiftCard() {
      if (!this.giftCardRecipientEmail) return;
      this.giftCardDrawerLoading = true;

      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.currentVariantId,
            quantity: 1,
            properties: {
              '__shopify_send_gift_card_to_recipient': true,
              'Recipient email': this.giftCardRecipientEmail,
              'Recipient name': this.giftCardRecipientName,
              'Message': this.giftCardMessage,
              'Send on': this.giftCardSendOn,
            }
          })
        });

        const data = await res.json();

        if (!res.ok) {
          this.errorMessage = data.description;
          this.errorAlert = true;
          return;
        }

        await Alpine.store('cart').updateCart();
        Alpine.store('dialog').close('gift-card-drawer');
      } catch (e) {
        console.error(e);
      } finally {
        this.giftCardDrawerLoading = false;
      }
    },
  };
}
